import React, { useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import { useFormikContext } from 'formik';
import { UpdateAccountInput, User } from '@elevator/ui';
import useStyles from './styles';
import { PageHeader } from 'molecules';
import { AccountSettingsForm } from 'molecules';
import ChangePasswordForm from './ChangePasswordForm';

/**
 * AccountSettings Component
 *
 * Layout for the account settings page. Renders page header, account form (via
 * AccountSettingsForm molecule), and the change-password modal dialog.
 *
 * Used in:
 * - `AccountSettings` layout index file wrapped in Formik provider.
 *
 * @param props - Component props
 * @param props.currentUser - The currently logged-in user object.
 * @param props.handleBlur - Callback for field blur (tracks touched fields for partial updates).
 */
const AccountSettings = (props: AccountSettingsProps): React.JSX.Element => {
  const { isValid, isSubmitting } = useFormikContext<UpdateAccountInput>();
  const { handleBlur, currentUser } = props;
  const { classes } = useStyles();

  const [passwordModalOpen, setPasswordModalOpen] = React.useState(false);
  const togglePasswordModalOpen = useCallback(() => {
    setPasswordModalOpen((prev) => !prev);
  }, []);

  return (
    <Stack width="100%" direction="column" spacing={3} data-layout="AccountSettings">
      <PageHeader title="ACCOUNT SETTINGS" divider />
      <AccountSettingsForm
        currentUser={currentUser}
        onBlur={handleBlur}
        onChangePasswordClick={togglePasswordModalOpen}
        submitDisabled={!isValid || isSubmitting}
      />
      <Dialog
        open={passwordModalOpen}
        onClose={togglePasswordModalOpen}
        slotProps={{
          paper: {
            className: classes.dialogPaper,
          },
        }}
      >
        <ChangePasswordForm
          togglePasswordModalOpen={togglePasswordModalOpen}
          {...props}
        />
      </Dialog>
    </Stack>
  );
};

export type AccountSettingsProps = {
  currentUser: User;
  handleBlur: (field: string, value: string) => void;
};

export default AccountSettings;
